// src/App.tsx
import { ThemeProvider, theme } from "@primer/react";
import deepmerge from "deepmerge";
import { useEffect } from "react";
import "react-loading-skeleton/dist/skeleton.css";
import { BrowserRouter as Router, useLocation } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import SuccessNotification from './components-ui/notification/SuccessNotification';

import BaseStyles from "./BaseStyles";
import { LazyPage } from "./components-ui/loadable";
import { CommonProvider } from "./contexts/common";
import { HubProvider } from "./contexts/HubProvider";
import { useAppDispatch } from "./hooks/useAppDispatch";
import { useAppSelector } from "./hooks/useAppSelector";
import * as authReducer from "./faetures/auth/authSlice";

// ✅ Shell layout mới (rail + menu + topbar + workspace)
import AppShell from "./layout/AppShell/AppShell";
import { usePermission } from "./hooks/usePermission";
import NoAccessPage from "./router/NoAccessPage";
import httpClient from './services/httpService';

const AuthorizedContent = LazyPage(import("./router/AuthorizedContent"));
const UnAuthorizedContent = LazyPage(import("./router/UnAuthorizedScreenSso"));

const AuthenticationContent = () => {
    const { pathname } = useLocation();
    const { isAuthenticated } = useAppSelector((state) => state.auth);
    const { hasApplicationAccess } = usePermission();

    if (pathname === '/signin-oidc' || pathname === '/signout-oidc' || !isAuthenticated) {
        return <UnAuthorizedContent />;
    }
    if (!hasApplicationAccess) return <NoAccessPage />;
    return (
        <HubProvider>
            <AppShell>
                <AuthorizedContent />
            </AppShell>
        </HubProvider>
    );
};

const customTheme = deepmerge(
    {
        ...theme,
        fontSizes: [
            "11px",
            "13px",
            "16px",
            "20px",
            "24px",
            "32px",
            "40px",
            "48px",
            "56px"
        ]
    },
    {
        colorSchemes: {
            light: {
                colors: {
                    accent: {
                        fg: "#072f1f",
                        emphasis: "#072f1f"
                    },
                    btn: {
                        primary: {
                            bg: "#072f1f",
                            focusBg: "#072f1f",
                            hoverBg: "#072f1f",
                            selectedBg: "#072f1f",
                            disabledBg: "rgba(37, 99, 235,0.6)"
                        }
                    }
                }
            }
        }
    }
);

function App() {
    const dispatch = useAppDispatch();

    useEffect(() => {
        const persistedAuth = localStorage.getItem("auth");
        if (!persistedAuth) return;
        if (localStorage.getItem('auth_provider') !== 'ictu') {
            ['auth', 'access_token', 'refresh_token', 'sso_id_token'].forEach(key => localStorage.removeItem(key));
            return;
        }

        try {
            const authData = JSON.parse(persistedAuth);
            const persistedUser = authData?.user;
            const persistedUserId = persistedUser?.id ?? persistedUser?.sub;

            if (!persistedUser || !persistedUserId) {
                localStorage.removeItem("auth");
                localStorage.removeItem("access_token");
                localStorage.removeItem("refresh_token");
                return;
            }

            dispatch(
                authReducer.loginSuccess({
                    payload: {
                        token_info: {
                            access_token: authData.accessToken,
                            refresh_token: authData.refreshToken
                        },
                        profile: authData.user
                    },
                    type: ""
                })
            );
            localStorage.setItem("access_token", authData.accessToken ?? "");
            localStorage.setItem("refresh_token", authData.refreshToken ?? "");
            // Refresh menu assignments on reload instead of keeping the login snapshot indefinitely.
            void httpClient.get('/api/account/info').then(response => {
                const profile = response.data?.is_success ? response.data.data : undefined;
                const currentAuth = localStorage.getItem('auth');
                if (!currentAuth || !profile || String(profile.id) !== String(authData.user.id)) return;
                const current = JSON.parse(currentAuth);
                if (String(current.user?.id) !== String(profile.id)) return;
                dispatch(authReducer.setUserProfile(profile));
                localStorage.setItem('auth', JSON.stringify({...current, user: profile}));
            }).catch(() => {
                // Keep the existing session on a temporary connection failure.
            });
        } catch (error) {
            console.error("Failed to parse auth data from localStorage", error);
        }
    }, [dispatch]);

    return (
        <Router>
            <CommonProvider>
                <ThemeProvider theme={customTheme} colorMode={"light"}>
                    <BaseStyles>
                        <Toaster
                            position="top-right"
                            toastOptions={{ duration: 4000 }}
                            reverseOrder={false}
                        />

                        <AuthenticationContent />
                        <SuccessNotification />
                    </BaseStyles>
                </ThemeProvider>
            </CommonProvider>
        </Router>
    );
}

export default App;
